import { UnexpectedError } from 'common/errors';
import teamService from 'common/services/team';
import React, { useState } from 'react';
import { useAsync } from 'react-async-hook';
import Button from 'common/components/Button';
import DefaultPageLoader from 'common/components/DefaultPageLoader';
import AuthenticatedLayout from 'common/layouts/AuthenticatedLayout';
import styles from './index.module.scss';

const InviteToTeam = () => {
  const getTeamTask = useAsync(teamService.getTeam, []);
  const team = getTeamTask.result;
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    setIsSuccess(false);

    try {
      await teamService.inviteTeamMember(email);
      setIsSuccess(true);
    } catch (err: any) {
      setError(err.message || new UnexpectedError().message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.content} data-testid="page-id-invite-team-member">
      {getTeamTask.loading && <DefaultPageLoader />}
      {!getTeamTask.loading && (
        <>
          <h1>Invite to {team?.name}</h1>

          <form onSubmit={handleSubmit}>
            {error && (
              <div data-testid="form-error" className={styles.errorBox}>
                {error}
              </div>
            )}
            {isSuccess && (
              <div 
                className={styles.successMessage}
                data-testid="invite-success-alert"
                title="Invite Sent"
              >
                <strong>Invite Sent</strong>
                <p>The user has been invited to join your team.</p>
              </div>
            )}
            <div className={styles.formContents}>
              <p>
                Enter the email of the WHOOP member you want to invite to your
                team. Please note that you should use the email address
                associated with their WHOOP account.
              </p>

              <div className={styles.emailInputWrapper}>
                <fieldset>
                  <legend>
                    Email<span>*</span>
                  </legend>
                  <input
                    id="email-input"
                    type="text"
                    name="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    aria-label="Email"
                    required
                    data-testid="email-input"
                    className={styles.emailInput}
                  />
                </fieldset>
              </div>
            </div>

            <Button
              type="submit"
              variant="primary"
              theme="dark"
              disabled={!email || isSubmitting || !email.trim()}
              data-testid="invite-button"
            >
              {isSubmitting ? 'Inviting...' : 'Invite'}
            </Button>
          </form>
        </>
      )}
    </div>
  );
};

export default () => (
  <AuthenticatedLayout pageIdentifier="invite-team-member">
    <InviteToTeam />
  </AuthenticatedLayout>
);
